import { queryClient } from 'lib/react-query';
import type { PackEntry } from 'types/shared';
import type { CreativesByHypothesis } from 'features/creatives';
import creativesQueryKeys from '../../creatives/api/queryKeys';
import type { HypothesisCreativeEntry } from '../types/hypothesis';
import { SINGLE_AD_TYPE } from '../constants';

export const MIXED_PACK_TAG = 'Mixed pack';
export const MIXED_PACK_KEY = '__mixed_pack__';

export const isMixedPackTag = (tag: string): boolean =>
  tag.trim().toLowerCase() === MIXED_PACK_TAG.toLowerCase();

export type MixedPackEntry = Pick<PackEntry, 'tag' | 'isMixedPack'>;

export const isMixedPackEntry = (pack: MixedPackEntry): boolean =>
  pack.isMixedPack === true || isMixedPackTag(pack.tag);

/** Maps a pack tag back to the hypothesis card key used in grouped views. */
export const packHypothesisKeyFromTag = (tag: string): string =>
  isMixedPackTag(tag) ? MIXED_PACK_KEY : tag;

export const packHypothesisKeyFromPack = (pack: MixedPackEntry): string => {
  if (isMixedPackEntry(pack)) return MIXED_PACK_KEY;
  return packHypothesisKeyFromTag(pack.tag);
};

export const selectionSpansMultipleHypotheses = (
  selectedIds: readonly string[],
  idToHypothesis: Map<string, string>,
): boolean => {
  const hypotheses = new Set<string>();
  for (const id of selectedIds) {
    const hypothesis = idToHypothesis.get(id);
    hypotheses.add(hypothesis ?? `__unknown_hypothesis__:${id}`);
    if (hypotheses.size > 1) return true;
  }
  return false;
};

export const getMixedPackCreativeIds = (
  packsWithCreatives: readonly Pick<PackEntry, 'tag' | 'creatives' | 'isMixedPack'>[],
): Set<string> => {
  const ids = new Set<string>();
  for (const pack of packsWithCreatives) {
    if (!isMixedPackEntry(pack)) continue;
    for (const id of pack.creatives) {
      if (typeof id === 'string') ids.add(id);
    }
  }
  return ids;
};

export const getHypothesisPackedCreativeIds = (
  packsWithCreatives: readonly Pick<PackEntry, 'tag' | 'creatives' | 'isMixedPack'>[],
): Set<string> => {
  const ids = new Set<string>();
  for (const pack of packsWithCreatives) {
    if (isMixedPackEntry(pack)) continue;
    for (const id of pack.creatives) {
      if (typeof id === 'string') ids.add(id);
    }
  }
  return ids;
};

export const buildPackEntry = (
  tag: string,
  creatives: string[],
  options?: { isMixedPack?: boolean },
): PackEntry => ({
  tag,
  adType: SINGLE_AD_TYPE,
  creatives,
  filterCreativeByName: [],
  visual_touch_ups: true,
  ...(options?.isMixedPack ? { isMixedPack: true } : {}),
});

export const removeCreativeIdsFromPacks = (
  packs: readonly PackEntry[],
  creativeIds: readonly string[],
): PackEntry[] => {
  const idsToRemove = new Set(creativeIds);
  return packs.map((pack) => ({
    ...pack,
    creatives: pack.creatives.filter(
      (id): id is string => typeof id === 'string' && !idsToRemove.has(id),
    ),
  }));
};

export const removeCreativeIdsFromHypothesisPacks = (
  packs: readonly PackEntry[],
  creativeIds: readonly string[],
): PackEntry[] => {
  const idsToRemove = new Set(creativeIds);
  return packs.map((pack) => {
    if (isMixedPackEntry(pack)) return pack;
    return {
      ...pack,
      creatives: pack.creatives.filter(
        (id): id is string => typeof id === 'string' && !idsToRemove.has(id),
      ),
    };
  });
};

export const isSameHypothesisPackContext = (
  pack: MixedPackEntry,
  creativeHypothesis: string,
): boolean =>
  !isMixedPackEntry(pack) &&
  packHypothesisKeyFromPack(pack) === creativeHypothesis;

/** True when a non-mixed pack of the same hypothesis already holds this creative. */
export const isCreativePackedUnderHypothesis = (
  creativeId: string,
  hypothesis: string,
  packs: readonly PackEntry[],
): boolean => {
  for (const pack of packs) {
    if (isMixedPackEntry(pack)) continue;
    if (!pack.creatives.includes(creativeId)) continue;
    if (packHypothesisKeyFromPack(pack) === hypothesis) {
      return true;
    }
  }
  return false;
};

export const removeCreativeIdsFromOwnedHypothesisPacks = (
  packs: readonly PackEntry[],
  creativeIds: readonly string[],
  idToHypothesis: ReadonlyMap<string, string>,
): PackEntry[] => {
  const ids = new Set(creativeIds);
  return packs.map((pack) => {
    if (isMixedPackEntry(pack)) return pack;
    const packKey = packHypothesisKeyFromPack(pack);
    return {
      ...pack,
      creatives: pack.creatives.filter((id) => {
        if (typeof id !== 'string' || !ids.has(id)) return true;
        return packKey !== idToHypothesis.get(id);
      }),
    };
  });
};

export const getHypothesisCreativesIdToHypothesis = (): Map<string, string> => {
  const cachedHypothesisCreatives = queryClient
    .getQueryCache()
    .findAll({ queryKey: creativesQueryKeys.allCreativesWithHypothesis })
    .map((query) => query.state.data as CreativesByHypothesis<HypothesisCreativeEntry>)
    .filter(Boolean);

  const idToHypothesis = new Map<string, string>();
  for (const byHypothesis of cachedHypothesisCreatives) {
    for (const [hypothesis, creatives] of Object.entries(byHypothesis)) {
      for (const creative of creatives) {
        idToHypothesis.set(creative.id, hypothesis);
      }
    }
  }
  return idToHypothesis;
};

export const groupIdsByHypothesis = (
  selectedIds: readonly string[],
  idToHypothesis: Map<string, string>,
): Map<string, string[]> => {
  const grouped = new Map<string, string[]>();
  for (const id of selectedIds) {
    const hypothesis = idToHypothesis.get(id);
    if (!hypothesis) continue;
    const bucket = grouped.get(hypothesis);
    if (bucket) bucket.push(id);
    else grouped.set(hypothesis, [id]);
  }
  return grouped;
};

/** Splits ids into packCount groups; earlier packs get +1 when count does not divide evenly. */
export const splitIdsIntoPacks = (
  ids: readonly string[],
  packCount: number,
): string[][] => {
  if (packCount < 1 || packCount > ids.length) return [ids.slice()];

  const packs: string[][] = Array.from({ length: packCount }, () => []);
  let index = 0;
  const baseSize = Math.floor(ids.length / packCount);
  let extraPacks = ids.length % packCount;

  for (let packIdx = 0; packIdx < packCount; packIdx++) {
    const size = baseSize + (extraPacks > 0 ? 1 : 0);
    if (extraPacks > 0) extraPacks--;
    packs[packIdx] = ids.slice(index, index + size);
    index += size;
  }

  return packs;
};

export const defaultSplitPackCount = (creativeCount: number): number =>
  Math.min(2, creativeCount);

export const clampSplitPackCount = (
  value: number,
  creativeCount: number,
): number => Math.min(Math.max(1, value), creativeCount);

export const parseSplitPackCountInput = (
  rawValue: string,
  creativeCount: number,
): number => {
  const parsed = Number.parseInt(rawValue.trim(), 10);
  if (Number.isNaN(parsed)) {
    return defaultSplitPackCount(creativeCount);
  }
  return clampSplitPackCount(parsed, creativeCount);
};

export const isValidSplitPackCountInput = (
  rawValue: string,
  creativeCount: number,
): boolean => {
  const trimmed = rawValue.trim();
  if (!trimmed || !/^\d+$/.test(trimmed)) return false;
  const parsed = Number.parseInt(trimmed, 10);
  return parsed >= 1 && parsed <= creativeCount;
};
